import { useEffect, useState } from 'react';
import { Input, List, Modal, Spin, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { fetchWorkspaceSearch, type WorkspaceSearchResult } from '@/services/aiAgent.service';

export interface AIWorkspaceSearchProps {
  open: boolean;
  onClose: () => void;
}

export function AIWorkspaceSearch({ open, onClose }: AIWorkspaceSearchProps) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<WorkspaceSearchResult[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      setLoading(true);
      void fetchWorkspaceSearch(q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const onPick = (r: WorkspaceSearchResult) => {
    onClose();
    if (r.type === 'conversation') navigate(`/ai/workspace?conversationId=${r.id}`);
    else if (r.type === 'pin') navigate('/ai/dashboard');
    else if (r.type === 'report') navigate(`/ai/udq/${r.id}`);
  };

  return (
    <Modal
      title="Search workspace"
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
    >
      <Input
        autoFocus
        placeholder="Search conversations, pins, reports… (Ctrl+K)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Global workspace search"
      />
      <Spin spinning={loading} style={{ marginTop: 12, minHeight: 80 }}>
        <List
          dataSource={results}
          locale={{ emptyText: q ? 'No results' : 'Type to search' }}
          renderItem={(r) => (
            <List.Item style={{ cursor: 'pointer' }} onClick={() => onPick(r)}>
              <List.Item.Meta
                title={r.title}
                description={
                  <>
                    <Tag>{r.type}</Tag> {r.subtitle}
                  </>
                }
              />
            </List.Item>
          )}
        />
      </Spin>
    </Modal>
  );
}
